/* import __COLOCATED_TEMPLATE__ from './sq-option-radio.hbs'; */
/**
#/**
A Radio Button with a label

```example
@title Basic Usage
@description Pass a label and optional secondary label.

@hbs
{{sq-option-radio optionName="option-radio"
                  label="Regular"}}

{{sq-option-radio optionName="option-radio-selected"
                  selected=true
                  label="Selected"}}

{{sq-option-radio optionName="option-radio-disabled"
                  disabled=true
                  label="Regular (Disabled)"}}

{{sq-option-radio optionName="option-radio-disabled-selected"
                  selected=true
                  disabled=true
                  label="Selected (Disabled)"}}


```

```example
@title Radio Sets
@description Pass the same `fieldName` to multiple `sq-option-radio` components to
create a working radio set. Try the [sq-field-radio-set](./sq-field-radio-set.html)
first, before manually making a radio set with multiple `sq-option-radio` components.
Beware, if you have multiple radio sets on the page with the same `fieldName`, they
will act as if they are part of the same set, i.e. selecting an option from one set
will deselect all the options from that set and any other set with the same `fieldName`.

@hbs
{{sq-option-radio optionName="option-radio"
                  fieldName="radio-set"
                  label="Regular"}}

{{sq-option-radio optionName="option-radio-selected"
                  fieldName="radio-set"
                  selected=true
                  label="Selected"}}

{{sq-option-radio optionName="option-radio-disabled"
                  fieldName="radio-set"
                  disabled=true
                  label="Regular (Disabled)"}}

{{sq-option-radio optionName="option-radio-disabled-selected"
                  fieldName="radio-set"
                  selected=true
                  disabled=true
                  label="Selected (Disabled)"}}


```

```example
@title With Secondary Text
@description You can pass a secondary label to the radio

@hbs
{{sq-option-radio optionName="option-radio-with-secondary-label"
                  label="Regular"
                  labelSecondary="Default"}}

{{sq-option-radio optionName="option-radio-with-secondary-label-selected"
                  selected=true
                  label="Selected"
                  labelSecondary="Default"}}


{{sq-option-radio optionName="option-radio-with-secondary-label-disabled"
                  disabled=true
                  label="Regular"
                  labelSecondary="Disabled"}}

{{sq-option-radio optionName="option-radio-with-secondary-label-disabled-selected"
                  selected=true disabled=true
                  label="Selected"
                  labelSecondary="Disabled"}}
```

```example
@title Contextual Labels
@description If you need to do some sort of fancy formatting for your labels, you can output each one
separately using contextual components

@hbs
{{#sq-option-radio name="option-radio-block" as |radio|}}
  {{#radio.label}}
    {{svgs/alert-icon}} Oh No, something is wrong
  {{/radio.label}}
  {{#radio.label-secondary}}
    Please correct this <em>egregious</em> error.
  {{/radio.label-secondary}}
{{/sq-option-radio}}

```

```example
@title Block Content
@description You can also yield completely custom content in addition to the primary
and secondary labels by passing a block directly to the radio option

@hbs
{{#sq-option-radio name="option-radio-block"
                   label="Select this custom option"}}
  <input class="input" type="text" placeholder="some kind of extra field">
{{/sq-option-radio}}

```
@param {String} optionName - A name for this option.
@param {String} fieldName - A name for the set that this radio button belongs to.
@param {String} label - The primary label for this field
@param {String} labelSecondary - Optional. Secondary label for this field.
@param {Any} value - A value that this radio represents (NOT the radio's state. This should be a string and not a boolean. Use `selected` for the state)
@param {Boolean/String} [selected=false] - true, false or 'indeterminate'
@param {Boolean} [disabled=false] - Disable the radio
@param {Number} [tabindex=0] - passes through to the input giving it a tabindex for keyboard navigation
@param {Function} action - An action to perform when the value of 'selected' changes. Is passed the new 'selected' value of the radio
*/

import Component from '@ember/component';
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  tagName: 'label',
  classNames: ['option', 'option--radio'],
  classNameBindings: ['selected:option--is-selected', 'selected:radio-option--is-selected', 'disabled:option--is-disabled'],
  attributeBindings: ['data-test-option-radio', 'data-test-option-radio-selected'],
  'data-test-option-radio': computed('label', 'value', function () {
    return this.get('label') || this.get('value') || true;
  }),
  'data-test-option-radio-selected': readOnly('selected'),
  optionName: null,
  selected: false
});